import React, { useMemo, useState } from 'react';
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { InformationCircleIcon, DevicePhoneMobileIcon, ChartBarIcon } from '@heroicons/react/24/outline';
import { useTelemetriaUso } from '../../hooks/useTelemetria';
import { fmtNumero } from '../../utils/formatters';
import { rotuloTela } from '../../utils/rotuloTela';
import { preencherDiasZerados } from '../../utils/seriesTelemetria';
import { deveAvisarColetaRecente, deveAvisarSemDadosMobile } from '../../utils/avisosUso';
import type { ContagemPorChave } from '../../types/auditoria';
import { ToggleIncluirAdmin } from './ToggleIncluirAdmin';

/** Teto do clamp `[1,365]` do backend — a sonda de mobile usa a janela MÁXIMA
 *  de propósito (ver comentário abaixo, no uso do hook). */
const DIAS_SONDA_MOBILE = 365;

const PERIODOS = [7, 30, 90] as const;
const ORIGENS = [
  { v: '', label: 'Todas' },
  { v: 'web', label: 'Web' },
  { v: 'mobile', label: 'Mobile' },
] as const;

const segBtn = (ativo: boolean) =>
  `rounded-iris-sm px-2.5 py-1 text-xs font-medium transition-colors ${
    ativo ? 'bg-accent text-white' : 'border border-line bg-surface text-tx-2 hover:bg-surface-2'
  }`;

/** "YYYY-MM-DD" -> "DD/MM". Fatiado direto da string (sem passar por `Date`,
 *  que reformataria no fuso local e poderia mostrar o dia anterior). */
const fmtDiaCurto = (iso: string): string => {
  const [, m, d] = iso.split('-');
  return `${d}/${m}`;
};

const fmtDiaCompleto = (iso: string): string => {
  const [y, m, d] = iso.split('-');
  return `${d}/${m}/${y}`;
};

const tooltipStyle = {
  background: 'var(--surface)',
  border: '1px solid var(--border)',
  borderRadius: 8,
  fontSize: 12,
  color: 'var(--text)',
};

/** Ranking em lista (não Recharts): rótulos de tela são texto humano — às
 *  vezes longo — e a lista pode chegar a 30 linhas; um eixo Y categórico
 *  ficaria espremido. Molde de `RankingTreemapMobile` (barra proporcional
 *  fina sob o rótulo), sem o drill-down que não se aplica aqui. */
const RankingLista: React.FC<{ itens: ContagemPorChave[]; rotular?: (chave: string) => string }> = ({ itens, rotular }) => {
  if (itens.length === 0) return <p className="p-3 text-xs text-tx-3">Sem dados no período.</p>;
  // Backend já devolve ordenado desc (Take 30) — o primeiro é o maior.
  const max = itens[0].contagem || 1;
  return (
    <ul className="flex flex-col gap-2">
      {itens.map((it) => {
        const label = rotular ? rotular(it.chave) : it.chave;
        const pct = (it.contagem / max) * 100;
        return (
          <li key={it.chave}>
            <div className="flex items-baseline justify-between gap-2">
              <span className="truncate text-xs text-tx" title={label}>{label}</span>
              <span className="shrink-0 text-xs font-semibold tabular-nums text-tx-2">{fmtNumero(it.contagem)}</span>
            </div>
            <div className="mt-1 h-1.5 overflow-hidden rounded-full bg-surface-2">
              <div className="h-full rounded-full bg-accent" style={{ width: `${Math.max(pct, 2)}%` }} />
            </div>
          </li>
        );
      })}
    </ul>
  );
};

/** Aba "Uso" — telemetria agregada (`GET /api/telemetria/uso`). Volume de uso
 *  (quantas vezes, por quem, em que dia), distinta de auditoria (eventos de
 *  negócio) e logs (falha técnica). */
export const AbaUso: React.FC = () => {
  const [dias, setDias] = useState<number>(30);
  const [origem, setOrigem] = useState<string>('');
  // Usuário `admin` (a conta do suporte) fora por default — cada atendimento
  // entrava no ranking de telas/recursos e em "usuários ativos" como se fosse
  // uso do cliente.
  const [incluirAdmin, setIncluirAdmin] = useState(false);

  const { data, isLoading, error } = useTelemetriaUso(dias, origem || undefined, incluirAdmin);

  // Sonda de janela MÁXIMA (365d, teto do clamp), independente do período
  // selecionado: a pergunta é "existe QUALQUER linha mobile alguma vez?", não
  // "houve mobile nos últimos 7/30/90 dias" — com o `dias` do usuário, quem só
  // não abriu uma janela estreita naquele recorte via falso-positivo mesmo com
  // mobile coletando há meses. Mesma queryKey scheme dedupa com a consulta
  // acima quando o filtro do usuário já é 'mobile' E `dias` já é 365.
  // A sonda INCLUI o admin de propósito: a pergunta é se a coleta mobile já
  // aconteceu alguma vez, e uma linha do suporte prova isso tão bem quanto
  // qualquer outra — filtrá-lo aqui acusaria "sem coleta" com a coleta viva.
  const { data: checagemMobile } = useTelemetriaUso(DIAS_SONDA_MOBILE, 'mobile', true);
  const semDadosMobile = deveAvisarSemDadosMobile(checagemMobile);

  const coletaRecente = deveAvisarColetaRecente(data?.primeiroDia, dias);

  // Guarda também o estado pending-paused (query offline: isLoading=false,
  // error=null, data=undefined) — sem isso o ramo de gráficos abaixo era
  // alcançado com `data` ausente. `telas`/`recursos`/`ativosPorDia` já nascem
  // com fallback `[]`, então usá-los aqui (em vez de `data!.x`) tanto resolve
  // a guarda quanto elimina os non-null asserts nos gráficos abaixo.
  const telas = data?.telas ?? [];
  const recursos = data?.recursos ?? [];
  const ativosPorDia = useMemo(
    () => preencherDiasZerados(data?.ativosPorDia ?? [], dias),
    [data?.ativosPorDia, dias],
  );
  const semDadosGeral = !isLoading && !error && telas.length === 0 && recursos.length === 0 && (data?.ativosPorDia ?? []).length === 0;

  return (
    <div className="flex h-full flex-col gap-3 overflow-auto">
      <div className="flex flex-wrap items-center gap-x-5 gap-y-2">
        <div className="flex items-center gap-2">
          <span className="text-[10px] uppercase tracking-wide text-tx-2">Período</span>
          <div className="flex items-center gap-1" role="radiogroup" aria-label="Janela de dias">
            {PERIODOS.map((p) => (
              <button key={p} type="button" onClick={() => setDias(p)} className={segBtn(dias === p)}>
                {p}d
              </button>
            ))}
          </div>
        </div>

        <div className="flex items-center gap-2">
          <span className="text-[10px] uppercase tracking-wide text-tx-2">Origem</span>
          <div className="flex items-center gap-1" role="radiogroup" aria-label="Origem do acesso">
            {ORIGENS.map((o) => (
              <button key={o.v} type="button" onClick={() => setOrigem(o.v)} className={segBtn(origem === o.v)}>
                {o.label}
              </button>
            ))}
          </div>
        </div>

        <ToggleIncluirAdmin valor={incluirAdmin} onChange={setIncluirAdmin} />
      </div>

      {/* Aviso honesto 1: gráfico curto porque a coleta é recente, não porque
          "ninguém usa o sistema" — a telemetria começou na Fase 2, não na
          instalação do cliente. */}
      {coletaRecente && data?.primeiroDia && (
        <div className="flex items-center gap-2 rounded-iris border border-warning-border bg-warning-soft px-3 py-2 text-xs text-warning">
          <InformationCircleIcon className="h-4 w-4 shrink-0" />
          <span>Coletando desde {fmtDiaCompleto(data.primeiroDia)} — período curto ainda, não é ausência de uso.</span>
        </div>
      )}

      {/* Aviso honesto 2: sem isto, "Todas"/"Web" pareceriam 100% web por
          conclusão, quando pode ser só ausência de coleta mobile. Fica só no
          fato observável (sonda de 365d sem nenhuma linha) — o front não tem
          como verificar se/quando uma fase de deploy aconteceu. */}
      {semDadosMobile && (
        <div className="flex items-center gap-2 rounded-iris border border-warning-border bg-warning-soft px-3 py-2 text-xs text-warning">
          <DevicePhoneMobileIcon className="h-4 w-4 shrink-0" />
          <span>Nenhum acesso de tela estreita registrado ainda.</span>
        </div>
      )}

      {isLoading ? (
        <p className="p-6 text-center text-sm text-tx-3">Carregando uso...</p>
      ) : error ? (
        <p className="p-6 text-center text-sm text-danger">Erro ao carregar a telemetria de uso.</p>
      ) : semDadosGeral ? (
        <div className="flex flex-col items-center justify-center gap-2 rounded-iris-lg border border-line bg-surface p-10 text-center text-sm text-tx-3">
          <ChartBarIcon className="h-10 w-10 text-tx-3 opacity-50" />
          <span>Nenhum uso registrado nos últimos {dias} dias{origem ? ` (origem ${origem})` : ''}.</span>
        </div>
      ) : (
        <>
          <div className="rounded-iris-lg border border-line bg-surface p-4">
            <h3 className="mb-2 text-[13px] font-semibold text-tx">Usuários ativos por dia</h3>
            <div style={{ width: '100%', height: 200 }}>
              <ResponsiveContainer>
                <BarChart data={ativosPorDia} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
                  <CartesianGrid stroke="var(--border)" strokeDasharray="3 3" vertical={false} />
                  <XAxis
                    dataKey="dia"
                    tickFormatter={fmtDiaCurto}
                    tick={{ fill: 'var(--text-3)', fontSize: 10.5 }}
                    axisLine={{ stroke: 'var(--border)' }}
                    tickLine={false}
                  />
                  <YAxis
                    allowDecimals={false}
                    tick={{ fill: 'var(--text-3)', fontSize: 10.5 }}
                    axisLine={false}
                    tickLine={false}
                    width={32}
                  />
                  <Tooltip
                    contentStyle={tooltipStyle}
                    labelStyle={{ color: 'var(--text-2)' }}
                    labelFormatter={(v) => fmtDiaCompleto(String(v))}
                    formatter={(v) => [fmtNumero(Number(v)), 'Usuários ativos']}
                    cursor={{ fill: 'var(--surface-2)' }}
                  />
                  <Bar dataKey="contagem" fill="var(--accent)" radius={[3, 3, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
            <div className="rounded-iris-lg border border-line bg-surface p-4">
              <h3 className="mb-2 text-[13px] font-semibold text-tx">Telas mais abertas</h3>
              <RankingLista itens={telas} rotular={rotuloTela} />
            </div>

            <div className="rounded-iris-lg border border-line bg-surface p-4">
              <h3 className="mb-2 text-[13px] font-semibold text-tx">Recursos mais acionados</h3>
              <RankingLista itens={recursos} />
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default AbaUso;
